'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from './button';
import { GoArrowDown } from 'react-icons/go';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

interface SubpageHeadingProps {
  title: string;
  slug: string;
}

export function SubpageHeading({ title, slug }: SubpageHeadingProps) {
  const { t } = useTranslations();

  function scrollDown() {
    const scrollDistance = window.innerWidth <= 640 ? 350 : 500;
    window.scrollTo({
      top: window.scrollY + scrollDistance,
      behavior: 'smooth',
    });
  }

  return (
    <section className={cn('relative w-full bg-[#0B2649] pt-[72px] sm:pt-20 md:pt-28')}>
      <Image
        src={'/images/header-triangle.svg'}
        alt="Design"
        width={0}
        height={0}
        className="absolute -top-[20px] left-0 z-[1] h-11 w-auto sm:h-12 md:-top-[40px] md:h-[91px]"
      />
      <Image
        src={'/images/subpage-overlay.jpg'}
        alt={title}
        width={0}
        height={0}
        className="absolute inset-0 h-full w-full object-cover object-center opacity-65 mix-blend-luminosity"
        sizes="100vw"
      />
      <div className={cn('custom-section', 'relative z-10')}>
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-start justify-start gap-2.5 sm:gap-3 md:gap-4 lg:gap-5',
          )}
        >
          <div className="flex items-center justify-start gap-2">
            <Link
              prefetch={false}
              href={ROUTES.HOME()}
              className="text-sm font-semibold tracking-tight text-white/70 sm:text-base"
            >
              {t('text160')}
            </Link>
            <span className="text-sm font-semibold tracking-tight text-white/30 sm:text-base">
              /
            </span>
            <Link
              prefetch={false}
              href={`/${slug}`}
              className="text-sm font-semibold tracking-tight text-white sm:text-base"
            >
              {title}
            </Link>
          </div>
          <h1
            data-ea-visual-tag="h2"
            className="text-3xl font-bold -tracking-tight text-white sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl"
          >
            {title}
          </h1>
        </div>
      </div>
      <div className="relative z-10 w-full">
        <div
          className={cn(
            'custom-container',
            'absolute bottom-0 left-1/2 z-10 flex -translate-x-1/2 items-end justify-end px-5 sm:bottom-4 lg:bottom-8 xl:px-0',
          )}
        >
          <Button onClick={scrollDown} variant={'outline'}>
            {t('text2404')} <GoArrowDown size={20} />
          </Button>
        </div>
        <Image
          src={'/images/body-triangle.svg'}
          alt="Design"
          width={0}
          height={0}
          className="h-[90px] w-full translate-y-[1px] object-cover md:h-auto"
        />
      </div>
    </section>
  );
}
